import { Dialog, Divider, Stack } from "@mui/material";
import React, { useCallback, useEffect, useId, useRef } from "react";
import { Accept } from "react-dropzone";

import { useSnackbar } from "@/components/ui-v2/Snackbar/useSnackbar";
import { UploadableModality } from "@/constants/dropzone";

import { DialogHeader } from "../DialogHeader/DialogHeader";
import { useVideoEditorDialog } from "../VideoEditorDialog/hooks/useVideoEditorDialog";
import { VideoEditorDialog } from "../VideoEditorDialog/VideoEditorDialog";
import { FilePickerContent } from "./components/FilePickerContent/FilePickerContent";
import { FilePickerFooter } from "./components/FilePickerFooter/FilePickerFooter";
import { useFilePickerPaste } from "./hooks/useFilePickerPaste";
import { useFilePickerState } from "./state";
import { Category, FilepickerFile } from "./types";

interface FilePickerProps {
  open: boolean;
  categories: Category[];
  onClose: () => void;
  onFilesSelect: (files: FilepickerFile[]) => void;
  accept: Accept;
  modality: UploadableModality;
  maxFiles?: number;
}

export const FilePicker = (props: FilePickerProps) => {
  const {
    open,
    categories,
    accept,
    onClose,
    onFilesSelect,
    maxFiles,
    modality,
  } = props;
  const instanceId = useId();
  const { showError } = useSnackbar();

  const {
    initCategoryTab,
    selectedFiles,
    setMaxFiles,
    setAcceptedFileTypes,
    setModality,
    resetState,
    isSelectionConfirmed,
    setSelectionAsUnconfirmed,
    setInstanceId,
    currentInstanceId,
  } = useFilePickerState();

  const {
    showVideoEditor,
    closeVideoEditor,
    pendingFile: pendingVideoEditorFile,
    handleFileSelectedForEditing,
  } = useVideoEditorDialog();

  const handleVideoEditorConfirm = (editedFile: FilepickerFile) => {
    // Convert the video editor result back to a FilepickerFile
    // and add the extracted frame to the selected files
    const updatedFiles = [editedFile];
    onFilesSelect(updatedFiles);
    onClose();
    closeVideoEditor();
  };

  const handleEntered = useCallback(() => {
    initCategoryTab(categories[0]);
    setInstanceId(instanceId);

    setAcceptedFileTypes(accept);
    setModality(modality);

    if (typeof maxFiles !== "undefined") {
      setMaxFiles(maxFiles);
    }
  }, [
    categories,
    accept,
    maxFiles,
    modality,
    initCategoryTab,
    setAcceptedFileTypes,
    setMaxFiles,
    setModality,
    setInstanceId,
    instanceId,
  ]);

  // Returns whether the confirmation was consumed (selection handed off / dialog
  // closing). The auto-confirm effect uses this to decide whether to latch
  // `hasHandledConfirmationRef`: a `false` return leaves the latch clear so the
  // user can retry without reopening the picker.
  const handleConfirm = useCallback((): boolean => {
    if (currentInstanceId !== instanceId) {
      return false;
    }

    // Guard against confirming with no files. When the same picker is closed
    // and immediately reopened, the closing dialog's `resetState` and the
    // reopen's `handleEntered` (which clears `selectedFiles`) can race with an
    // upload's `setSelectionAsConfirmed`, briefly leaving the selection empty.
    // Confirming here would call `onFilesSelect([])` and close the dialog
    // without ever surfacing the follow-up flow (e.g. the reference type picker).
    if (selectedFiles.length === 0) {
      return false;
    }

    // Check if any selected files are videos
    const videoFiles = selectedFiles.filter((file) =>
      file.contentType?.startsWith("video/"),
    );

    if (videoFiles.length > 0 && modality === "IMAGE") {
      // Process non-video files normally
      const nonVideoFiles = selectedFiles.filter(
        (file) => !file.contentType?.startsWith("video/"),
      );

      if (nonVideoFiles.length > 0) {
        onFilesSelect(nonVideoFiles);
      }

      // Process first video file TODO: Handle multiple video files
      const videoFile = videoFiles[0];

      if (!videoFile.width || !videoFile.height || !videoFile.src) {
        showError("Unable to load video file.");
        // Clear the store confirmation flag so re-triggering (e.g. picking a
        // valid file and clicking Select again) works while the dialog stays
        // open; returning false keeps the effect's latch clear too.
        setSelectionAsUnconfirmed();
        return false;
      }

      const videoEditorFile = {
        width: videoFile.width,
        height: videoFile.height,
        videoSrc: videoFile.src,
      };

      handleFileSelectedForEditing(videoEditorFile);
      onClose();
      return true;
    }

    // If no video files or modality is not IMAGE, proceed normally
    onFilesSelect(selectedFiles);
    onClose();
    return true;
  }, [
    currentInstanceId,
    instanceId,
    onFilesSelect,
    selectedFiles,
    onClose,
    modality,
    handleFileSelectedForEditing,
    showError,
    setSelectionAsUnconfirmed,
  ]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && selectedFiles.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        handleConfirm();
      }
    },
    [handleConfirm, selectedFiles],
  );

  // Tracks whether the current confirmation has already been handled, so that
  // re-running the effect (e.g. once `selectedFiles` arrives after the confirm
  // flag flips) does not confirm the same selection twice. Reset when closed.
  const hasHandledConfirmationRef = useRef(false);

  useEffect(() => {
    if (!open) {
      hasHandledConfirmationRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (
      open &&
      isSelectionConfirmed &&
      currentInstanceId === instanceId &&
      selectedFiles.length > 0 &&
      !hasHandledConfirmationRef.current
    ) {
      // Only latch when the confirmation is actually consumed; a failed attempt
      // (e.g. a video that can't be loaded) returns false and stays retryable.
      hasHandledConfirmationRef.current = handleConfirm();
    }
  }, [
    isSelectionConfirmed,
    currentInstanceId,
    instanceId,
    selectedFiles,
    open,
    handleConfirm,
  ]);

  useFilePickerPaste({
    enabled: open,
  });

  return (
    <>
      <Dialog
        data-testid="file-picker-dialog"
        fullWidth
        maxWidth="lg"
        open={open}
        slotProps={{
          paper: { sx: { height: "100%" } },
          transition: { onExited: resetState, onEntered: handleEntered },
        }}
        onClose={(_event, _reason) => onClose()}
        onKeyDown={handleKeyDown}
      >
        <Stack sx={{ height: "100%" }}>
          <DialogHeader title="Select File" onClose={onClose} />
          <Divider />
          <FilePickerContent categories={categories} />
          <Divider />
          <FilePickerFooter onClose={onClose} />
        </Stack>
      </Dialog>
      {!!pendingVideoEditorFile && (
        <VideoEditorDialog
          open={showVideoEditor && !!pendingVideoEditorFile}
          file={pendingVideoEditorFile}
          onClose={closeVideoEditor}
          onConfirm={handleVideoEditorConfirm}
        />
      )}
    </>
  );
};
